import { LinkedinFilled } from '@ant-design/icons';
import { Space, Card, Typography, Divider, Image, Spin, Tag, Button } from 'antd';
import moment from 'moment';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import careerjetLogo from '../../assets/careerjet.png';
import googleForJobsLogo from '../../assets/gfj.png';
import joobleLogo from '../../assets/jooble.png';
import netvagasLogo from '../../assets/netvagas.png';
import ErrorResult from '../../components/ErrorResult';
import OpportunityTabs from '../../components/OpportunityTabs';
import Page from '../../components/Page';
import PageContent from '../../components/PageContent';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { JobPostingId } from '../../types';
import { getFrontendManualTranslations } from '../../utils';
import styles from './OpportunityDetails.module.css';

const OpportunityDetails = () => {
  const { t } = useTranslation();

  const { tenant, id } = useTypedParams({ tenant: String, id: JobPostingId });
  const navigate = useTypedNavigate();

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const stagesPages = useInfiniteAPIQuery([
    `/stages/`,
    {
      params: {
        job_posting_id: id,
      },
    },
  ]);

  const stages = usePrefetchPages(stagesPages, (data) => data.results);

  const recruitersPages = useInfiniteAPIQuery([
    '/recruiters/',
    {
      params: {
        job_posting_id: id,
      },
    },
  ]);

  const config = useAPIQuery('/configs/tenant-configurations/');

  const recruiters = usePrefetchPages(recruitersPages, (data) => data.results);

  const netvagasExternalPublication =
    jobPosting.data?.external_publication && jobPosting.data.netvagas_external_publication;

  const googleForJobsExternalPublication =
    jobPosting.data?.external_publication && jobPosting.data.google_for_jobs_external_publication;

  const linkedinExternalPublication =
    jobPosting.data?.external_publication && jobPosting.data.linkedin_external_publication;

  const careerjetExternalPublication =
    jobPosting.data?.external_publication && jobPosting.data.careerjet_external_publication;

  const joobleExternalPublication =
    jobPosting.data?.external_publication && jobPosting.data.jooble_external_publication;

  return (
    <ErrorResult error={jobPosting.error || stagesPages.error}>
      <Page>
        <Spin spinning={jobPosting.isLoading || stagesPages.isLoading}>
          <Helmet>
            <title>
              {t('Resumo da vaga')} | {jobPosting.data?.name || '...'} |{' '}
              {t('Recrutamento e Seleção')}
            </title>
          </Helmet>
          <PageContent>
            <Outlet />
            <Card>
              <OpportunityTabs />
              <Typography.Title level={5} className={styles.title}>
                <Trans>Dados Gerais</Trans>
              </Typography.Title>
              <Divider className={styles.customDivider} />
              {jobPosting.data?.positions && (
                <Space direction="horizontal">
                  <Typography.Text className={styles.topics}>
                    <Trans>Posições:</Trans>
                  </Typography.Text>
                  <Typography.Text>{jobPosting.data?.positions}</Typography.Text>
                </Space>
              )}
              {jobPosting.data?.limit_days && (
                <Space direction="horizontal">
                  <Typography.Text className={styles.topics}>
                    <Trans>Em quanto tempo deseja finalizar a vaga?</Trans>
                  </Typography.Text>
                  <Typography.Text>
                    {jobPosting.data?.limit_days} <Trans>dias</Trans>
                  </Typography.Text>
                </Space>
              )}
              {recruiters.length > 0 && (
                <Space direction="horizontal">
                  <Typography.Text className={styles.topics}>
                    <Trans>Time de recrutamento:</Trans>
                  </Typography.Text>
                  <Typography.Text>
                    {recruiters.map((recruiter, idx) => (
                      <React.Fragment key={recruiter.user.id}>
                        <Typography.Text>{recruiter.user.full_name}</Typography.Text>
                        {idx < recruiters.length - 1 ? ', ' : ''}
                      </React.Fragment>
                    ))}
                  </Typography.Text>
                </Space>
              )}

              {jobPosting.data?.description && (
                <Space direction="vertical">
                  <Divider className={styles.customDivider} />
                  <Typography.Title level={5} className={styles.title}>
                    <Trans>Descrição da vaga</Trans>
                  </Typography.Title>
                  <div dangerouslySetInnerHTML={{ __html: jobPosting.data?.description || '' }} />
                </Space>
              )}
              <Divider className={styles.customDivider} />
              {(jobPosting.data?.country || jobPosting.data?.city || jobPosting.data?.state) && (
                <Space direction="horizontal" size="large">
                  {jobPosting.data?.country && (
                    <Space direction="horizontal">
                      <Typography.Text className={styles.topics}>
                        <Trans>País:</Trans>
                      </Typography.Text>
                      <Typography.Text>{jobPosting.data?.country}</Typography.Text>
                    </Space>
                  )}
                  {jobPosting.data?.city && (
                    <Space direction="horizontal">
                      <Typography.Text className={styles.topics}>
                        <Trans>Cidade:</Trans>
                      </Typography.Text>
                      <Typography.Text>{jobPosting.data?.city}</Typography.Text>
                    </Space>
                  )}
                  {jobPosting.data?.state && (
                    <Space direction="horizontal">
                      <Typography.Text className={styles.topics}>
                        <Trans>Estado:</Trans>
                      </Typography.Text>
                      <Typography.Text>{jobPosting.data?.state}</Typography.Text>
                    </Space>
                  )}
                </Space>
              )}

              {jobPosting.data?.work_model && (
                <Space direction="horizontal">
                  <Typography.Text className={styles.topics}>
                    <Trans>Modelo de trabalho:</Trans>
                  </Typography.Text>
                  {jobPosting.data?.work_model === 'HYBRID' && (
                    <Typography.Text>
                      <Trans>Híbrido</Trans>
                    </Typography.Text>
                  )}
                  {jobPosting.data?.work_model === 'IN_PERSON' && (
                    <Typography.Text>
                      <Trans>Presencial</Trans>
                    </Typography.Text>
                  )}
                  {jobPosting.data?.work_model === 'REMOTE' && (
                    <Typography.Text>
                      <Trans>Remoto</Trans>
                    </Typography.Text>
                  )}
                </Space>
              )}
              {jobPosting.data?.hire_model && (
                <Space direction="horizontal">
                  <Typography.Text className={styles.topics}>
                    <Trans>Modelo de contratação:</Trans>
                  </Typography.Text>
                  {jobPosting.data?.hire_model === 'APRENDICE' && (
                    <Typography.Text>
                      <Trans>Aprendiz</Trans>
                    </Typography.Text>
                  )}
                  {jobPosting.data?.hire_model === 'AUTONOMOUS_PJ' && (
                    <Typography.Text>
                      <Trans>Autônomo PJ</Trans>
                    </Typography.Text>
                  )}
                  {jobPosting.data?.hire_model === 'TEMPORARY' && (
                    <Typography.Text>
                      <Trans>Temporário</Trans>
                    </Typography.Text>
                  )}
                  {jobPosting.data?.hire_model === 'EFFECTIVE_CLT' && (
                    <Typography.Text>
                      <Trans>Efetivo CLT</Trans>
                    </Typography.Text>
                  )}
                  {jobPosting.data?.hire_model === 'INTERNSHIP' && (
                    <Typography.Text>
                      <Trans>Estágio</Trans>
                    </Typography.Text>
                  )}
                  {jobPosting.data?.hire_model === 'TRAINEE' && (
                    <Typography.Text>
                      <Trans>Trainee</Trans>
                    </Typography.Text>
                  )}
                  {jobPosting.data?.hire_model === 'COOPERATED' && (
                    <Typography.Text>
                      <Trans>Cooperado</Trans>
                    </Typography.Text>
                  )}
                  {jobPosting.data?.hire_model === 'ASSOCIATED' && (
                    <Typography.Text>
                      <Trans>Associado</Trans>
                    </Typography.Text>
                  )}
                  {jobPosting.data?.hire_model === 'TALENT_POOL' && (
                    <Typography.Text>
                      <Trans>Banco de Talentos</Trans>
                    </Typography.Text>
                  )}
                </Space>
              )}
              {jobPosting.data?.external_publication_start_at &&
                jobPosting.data?.external_publication_end_at && (
                  <Space direction="horizontal">
                    <Typography.Text className={styles.topics}>
                      <Trans>Período de divulgação:</Trans>
                    </Typography.Text>
                    <Typography.Text>
                      {moment(jobPosting.data?.external_publication_start_at)
                        .utc()
                        .format('DD/MM/YYYY')}
                      {' - '}
                      {moment(jobPosting.data?.external_publication_end_at)
                        .utc()
                        .format('DD/MM/YYYY')}
                    </Typography.Text>
                  </Space>
                )}
              {(linkedinExternalPublication ||
                netvagasExternalPublication ||
                googleForJobsExternalPublication ||
                careerjetExternalPublication ||
                joobleExternalPublication) && (
                <Space direction="vertical">
                  <Divider className={styles.customDivider} />
                  <Typography.Text className={styles.topics}>
                    <Trans>Publicado em:</Trans>
                  </Typography.Text>
                  <Space direction="horizontal">
                    {linkedinExternalPublication && (
                      <LinkedinFilled style={{ fontSize: 64, color: '#0E76A8' }} />
                    )}
                    {netvagasExternalPublication && (
                      <Image src={netvagasLogo} preview={false} width={200} />
                    )}
                    {googleForJobsExternalPublication && (
                      <Image src={googleForJobsLogo} preview={false} width={150} />
                    )}
                    {careerjetExternalPublication && (
                      <Image
                        src={careerjetLogo}
                        preview={false}
                        width={150}
                        alt={t('Logo do Careerjet')}
                      />
                    )}
                    {joobleExternalPublication && (
                      <Image
                        src={joobleLogo}
                        preview={false}
                        width={150}
                        alt={t('Logo do Jooble')}
                      />
                    )}
                  </Space>
                </Space>
              )}
              <Space direction="vertical">
                <Typography.Text className={styles.topics}>
                  <Trans>Etapas</Trans>:
                </Typography.Text>
                <Space direction="horizontal">
                  {stages.map((stage) => (
                    <Tag>
                      {getFrontendManualTranslations(
                        stage.name,
                        config.data?.t007_enableChangeLanguage || false
                      )}
                    </Tag>
                  ))}
                </Space>
                <Typography.Text>
                  <Trans>Veja uma análise completa por etapa</Trans>:
                </Typography.Text>
                <Button
                  type="primary"
                  onClick={() =>
                    navigate({
                      pathname: '/:tenant/opportunities/:id/manager_dashboard/',
                      params: { tenant, id },
                    })
                  }
                >
                  <Trans>Ver Dashboard da Vaga</Trans>
                </Button>
              </Space>
            </Card>
          </PageContent>
        </Spin>
      </Page>
    </ErrorResult>
  );
};

export default OpportunityDetails;
